/**
 * Generic host normalization and coverage, mirroring the API's `ApprovedDomainNormalizer` and
 * `OriginValidationHelper.HostMatchesWithSubdomain` so dashboard-side validation and the server's
 * save path never disagree.
 *
 * These live here rather than in `approvedDomain.ts` because two unrelated features now depend on
 * them: the ORB-458 visitor-tracking origin allowlist and the ORB-619 non-prospect email-domain
 * lists. Nothing about the rules is specific to either. `approvedDomain.ts` re-exports them under
 * its original names so existing call sites are untouched.
 */

// Multi-label host, each label 1–63 chars of [a-z0-9-] not starting/ending with '-', total ≤253.
// At least two labels, so single words ("localhost", "acme") and bare TLDs are rejected.
const HOST_PATTERN =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/

/**
 * Reduces `raw` to a bare lowercase host, or returns null for null/blank input, wildcards, embedded
 * whitespace, unparseable values, or anything that isn't a valid multi-label hostname.
 */
export function normalizeHost(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  // Wildcards and any internal whitespace are rejected outright — an entry is a single concrete
  // host, never a pattern.
  if (trimmed.includes('*') || /\s/.test(trimmed)) return null

  // Give a bare host a scheme so the URL parser has something absolute to work with; a value that
  // already carries a scheme parses as-is. Either way only the host survives (port/path/query drop).
  const candidate = trimmed.includes('://') ? trimmed : `https://${trimmed}`
  let host: string
  try {
    host = new URL(candidate).hostname.toLowerCase()
  } catch {
    return null
  }

  // Drop a leading www. so the registrable base is stored — matches the server's canonical form.
  if (host.startsWith('www.')) host = host.slice(4)

  return HOST_PATTERN.test(host) ? host : null
}

/**
 * Returns the entry that covers `host`, or null when none does: an entry covers itself and any
 * subdomain beneath it, so `acme.com` covers `blog.acme.com`. Entries are trimmed and lowercased
 * before comparison and blank entries never match. The covering entry is returned in its stored
 * form so callers can name it in the UI.
 *
 * Public suffixes are deliberately not special-cased: an entry of `github.io` covers every
 * `*.github.io` here exactly as it does at the API today.
 */
export function findCoveringHost(host: string, entries: readonly string[]): string | null {
  const candidate = host.trim().toLowerCase()
  if (!candidate) return null

  for (const entry of entries) {
    const approved = entry.trim().toLowerCase()
    if (!approved) continue
    if (candidate === approved || candidate.endsWith(`.${approved}`)) return entry
  }
  return null
}

/** True when any entry covers `host`. See `findCoveringHost`. */
export function isHostCoveredBy(host: string, entries: readonly string[]): boolean {
  return findCoveringHost(host, entries) !== null
}
